import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import ArrowRight from '@/presentation/assets/svg/ArrowRight.svg'
import Button from '@/presentation/components/Button'
import Header from '@/presentation/components/Header'
import { alterationOnboardingStorageKey } from '@/services/constants/localStorageKeys'
import { ROUTES } from '@/services/constants/routes'
import useNavigateWithOrderCode from '@/services/hooks/useNavigateWithOrderCode'
import {
  Alteration,
  useAlteration
} from '@/services/providers/AlterationContext'
import { useRoom } from '@/services/providers/RoomContext'
import checkLocalStorageKeyAndDo from '@/services/utils/checkLocalStorageKeyAndDo'

const AlterationPendingListPage: React.FC = () => {
  const [itemsWithRoom, setItemsWithRoom] = useState<Alteration[]>([])

  const { t } = useTranslation()

  const { pendingAlterations, startNewAlteration } = useAlteration()
  const { rooms } = useRoom()
  const navigate = useNavigateWithOrderCode()
  const { orderCode } = useParams()

  const mappedPendingAlterationItems = useMemo(() => {
    if (!itemsWithRoom?.length) return []

    return itemsWithRoom.map(alteration => {
      const room = rooms.find(room => room?.id === alteration?.room_id)
      const item = room?.items.find(item => item?.id === alteration?.item_id)

      return {
        ...alteration,
        room,
        item
      }
    })
  }, [itemsWithRoom])

  useEffect(() => {
    setItemsWithRoom(() => {
      return pendingAlterations.filter(alteration => !!alteration?.room_id)
    })
  }, [pendingAlterations, rooms])

  const handleNewAlteration = () => {
    if (
      orderCode &&
      checkLocalStorageKeyAndDo(alterationOnboardingStorageKey(orderCode))
    ) {
      navigate(ROUTES.ROOMS, true)

      return
    }
    navigate(ROUTES.ALTERATION_ONBOARDING)
  }

  const handleGoToRoomItem = (room_id: string, item_id: string) => {
    startNewAlteration(room_id, item_id)
    navigate(ROUTES.ALTERATION_FORM)
  }

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <Header
        previousRoute={ROUTES.ALTERATION_LIST}
        title={t('REGISTERED_REQUESTS', 'Solicitações registradas')}
      />

      <div className="flex w-full max-w-[682px] flex-col items-center px-4 py-2">
        {mappedPendingAlterationItems.map(alterationItem => (
          <div
            key={`${alterationItem.room_id}-${alterationItem.item_id}`}
            className="mb-4 flex w-full cursor-pointer flex-col rounded-xl border border-gray-400 bg-white shadow-md"
            onClick={() =>
              handleGoToRoomItem(
                alterationItem?.room_id,
                alterationItem?.item_id
              )
            }
          >
            <div
              className="h-20 w-full rounded-t-md bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(https://painel.dev.redevistorias.com.br/api/medias/75300A80-2E79-492F-8B3F-4B6239D1319F/75300A80-2E79-492F-8B3F-4B6239D1319F/100D9E1C-4F4B-11EB-B7C7-0242AC120004/Photos/4528515D-56C7-5231-9742-144DF49F8607.jpg)`
              }}
            />

            <div className="flex flex-col items-start justify-start gap-y-2 rounded-b-xl border-t-0 p-4 text-left">
              <div className="flex w-full items-center justify-between gap-y-2">
                <span className="text-xl font-semibold text-gray-600">
                  {alterationItem?.item?.id === 'building'
                    ? alterationItem?.item?.name
                    : `${alterationItem?.item?.name} | ${alterationItem?.room?.name}`}
                </span>

                <img src={ArrowRight} alt="Arrow right icon" />
              </div>

              <span className="text-sm text-gray-500">
                {alterationItem?.description}
              </span>
            </div>
          </div>
        ))}
        <Button
          className="mb-8 mt-4 flex w-full max-w-sm items-center justify-between"
          onClick={handleNewAlteration}
          colored
        >
          <span className="flex-1 text-center">
            {t('REGISTER_NEW_CHANGE', 'Registrar nova alteração')}
          </span>

          <svg
            width="14"
            height="14"
            transform="scale(-1 1)"
            viewBox="0 0 12 20"
            fill="none"
          >
            <path d="M11 1L2 10L3 11L11 19" stroke="white" strokeWidth="2" />
          </svg>
        </Button>

        {mappedPendingAlterationItems.length > 0 && (
          <Button
            className="mb-4 mt-8 w-full max-w-sm"
            onClick={() => navigate(ROUTES.ALTERATION_REGISTER)}
          >
            {t('SEND_REQUESTS', 'Enviar solicitações')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default AlterationPendingListPage
